import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Eye,
  Download,
  Quote,
  Users,
  Globe,
  Calendar,
  FileText,
  Database,
  Sparkles,
  ArrowUpRight,
  ArrowDownRight,
  Target,
  Award,
  BookOpen,
  Share2,
  RefreshCw
} from "lucide-react";

// Mock analytics data
const overviewMetrics = {
  totalViews: 1247892,
  viewsChange: 12.5,
  totalDownloads: 89456,
  downloadsChange: 8.3,
  totalCitations: 15234,
  citationsChange: 23.1,
  hIndex: 42,
  hIndexChange: 2,
  i10Index: 156,
  altmetricScore: 8945,
  altmetricChange: 15.7
};

const monthlyTrends = [
  { month: "Jul", views: 85000, downloads: 6200, citations: 1100 },
  { month: "Aug", views: 92000, downloads: 6800, citations: 1250 },
  { month: "Sep", views: 88000, downloads: 6500, citations: 1180 },
  { month: "Oct", views: 105000, downloads: 7800, citations: 1420 },
  { month: "Nov", views: 112000, downloads: 8200, citations: 1550 },
  { month: "Dec", views: 98000, downloads: 7100, citations: 1380 },
  { month: "Jan", views: 125000, downloads: 9500, citations: 1680 }
];

const topRecords = [
  {
    id: 1,
    title: "Machine Learning Applications in Climate Modeling",
    type: "Dataset",
    views: 45230,
    downloads: 3420,
    citations: 89,
    altmetric: 245
  },
  {
    id: 2,
    title: "Australian Biodiversity Genomics Database",
    type: "Collection",
    views: 38920,
    downloads: 2890,
    citations: 156,
    altmetric: 312
  },
  {
    id: 3,
    title: "Coral Reef Monitoring Time Series 2015-2024",
    type: "Dataset",
    views: 32450,
    downloads: 2150,
    citations: 67,
    altmetric: 189
  },
  {
    id: 4,
    title: "Indigenous Knowledge Documentation Project",
    type: "Collection",
    views: 28900,
    downloads: 1890,
    citations: 45,
    altmetric: 567
  },
  {
    id: 5,
    title: "Solar Energy Performance Metrics 2020-2024",
    type: "Dataset",
    views: 25600,
    downloads: 1650,
    citations: 78,
    altmetric: 134
  }
];

const geographicData = [
  { country: "Australia", percentage: 35, count: 436762 },
  { country: "United States", percentage: 22, count: 274536 },
  { country: "United Kingdom", percentage: 12, count: 149747 },
  { country: "Germany", percentage: 8, count: 99831 },
  { country: "China", percentage: 7, count: 87352 },
  { country: "Japan", percentage: 5, count: 62394 },
  { country: "Other", percentage: 11, count: 137270 }
];

const researcherMetrics = [
  {
    id: 1,
    name: "Dr. Sarah Chen",
    orcid: "0000-0002-1234-5678",
    publications: 45,
    citations: 2340,
    hIndex: 28,
    views: 156000
  },
  {
    id: 2,
    name: "Prof. James Mitchell",
    orcid: "0000-0003-2345-6789",
    publications: 78,
    citations: 4560,
    hIndex: 35,
    views: 234000
  },
  {
    id: 3,
    name: "Dr. Emma Watson",
    orcid: "0000-0001-3456-7890",
    publications: 32,
    citations: 1890,
    hIndex: 22,
    views: 98000
  },
  {
    id: 4,
    name: "Dr. Michael Lee",
    orcid: "0000-0002-4567-8901",
    publications: 56,
    citations: 3120,
    hIndex: 31,
    views: 178000
  }
];

const aiInsights = [
  {
    type: "trend",
    title: "Rising Interest in Climate Data",
    description: "Climate-related datasets have seen 45% increase in downloads over the past quarter. Consider highlighting these collections.",
    impact: "high"
  },
  {
    type: "opportunity",
    title: "Citation Gap Identified",
    description: "12 highly-viewed datasets have low citation counts. Adding DOIs and improving metadata could boost academic visibility.",
    impact: "medium"
  },
  {
    type: "benchmark",
    title: "Above Sector Average",
    description: "Your h-index of 42 exceeds the sector average of 28 for research data centers of similar size.",
    impact: "positive"
  },
  {
    type: "action",
    title: "Social Media Opportunity",
    description: "Altmetric scores indicate strong Twitter engagement. Consider creating data visualization threads for top datasets.",
    impact: "medium"
  }
];

export default function ResearchAnalyticsPage() {
  const [timeRange, setTimeRange] = useState("12months");
  const [activeTab, setActiveTab] = useState("overview");

  const maxTrend = Math.max(...monthlyTrends.map(t => t.views));

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <BarChart3 className="h-8 w-8 text-primary" />
            Research Analytics
          </h1>
          <p className="text-muted-foreground mt-1">
            Usage metrics, impact tracking, and research performance insights
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[180px]">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Time range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30days">Last 30 Days</SelectItem>
              <SelectItem value="90days">Last 90 Days</SelectItem>
              <SelectItem value="12months">Last 12 Months</SelectItem>
              <SelectItem value="alltime">All Time</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
          <Button variant="outline" size="icon">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <Eye className="h-8 w-8 text-blue-500" />
              <Badge variant={overviewMetrics.viewsChange > 0 ? "default" : "destructive"} className="gap-1">
                {overviewMetrics.viewsChange > 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                {Math.abs(overviewMetrics.viewsChange)}%
              </Badge>
            </div>
            <div className="mt-3">
              <p className="text-2xl font-bold">{(overviewMetrics.totalViews / 1000000).toFixed(2)}M</p>
              <p className="text-sm text-muted-foreground">Total Views</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <Download className="h-8 w-8 text-green-500" />
              <Badge variant={overviewMetrics.downloadsChange > 0 ? "default" : "destructive"} className="gap-1">
                {overviewMetrics.downloadsChange > 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                {Math.abs(overviewMetrics.downloadsChange)}%
              </Badge>
            </div>
            <div className="mt-3">
              <p className="text-2xl font-bold">{(overviewMetrics.totalDownloads / 1000).toFixed(1)}K</p>
              <p className="text-sm text-muted-foreground">Downloads</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <Quote className="h-8 w-8 text-purple-500" />
              <Badge variant={overviewMetrics.citationsChange > 0 ? "default" : "destructive"} className="gap-1">
                {overviewMetrics.citationsChange > 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                {Math.abs(overviewMetrics.citationsChange)}%
              </Badge>
            </div>
            <div className="mt-3">
              <p className="text-2xl font-bold">{(overviewMetrics.totalCitations / 1000).toFixed(1)}K</p>
              <p className="text-sm text-muted-foreground">Citations</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <Award className="h-8 w-8 text-orange-500" />
              <Badge variant="secondary" className="gap-1">
                +{overviewMetrics.hIndexChange}
              </Badge>
            </div>
            <div className="mt-3">
              <p className="text-2xl font-bold">{overviewMetrics.hIndex}</p>
              <p className="text-sm text-muted-foreground">h-Index</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <Share2 className="h-8 w-8 text-pink-500" />
              <Badge variant={overviewMetrics.altmetricChange > 0 ? "default" : "destructive"} className="gap-1">
                {overviewMetrics.altmetricChange > 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                {Math.abs(overviewMetrics.altmetricChange)}%
              </Badge>
            </div>
            <div className="mt-3">
              <p className="text-2xl font-bold">{(overviewMetrics.altmetricScore / 1000).toFixed(1)}K</p>
              <p className="text-sm text-muted-foreground">Altmetric Score</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" className="gap-2">
            <TrendingUp className="h-4 w-4" />
            Trends
          </TabsTrigger>
          <TabsTrigger value="records" className="gap-2">
            <FileText className="h-4 w-4" />
            Top Records
          </TabsTrigger>
          <TabsTrigger value="geographic" className="gap-2">
            <Globe className="h-4 w-4" />
            Geographic
          </TabsTrigger>
          <TabsTrigger value="researchers" className="gap-2">
            <Users className="h-4 w-4" />
            Researchers
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Trend Chart */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Usage Trends</CardTitle>
                <CardDescription>Monthly views, downloads, and citations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Simple bar chart visualization */}
                  <div className="flex items-end gap-2 h-48">
                    {monthlyTrends.map((trend, index) => (
                      <div key={index} className="flex-1 flex flex-col items-center gap-1">
                        <div className="w-full flex flex-col gap-1">
                          <div
                            className="w-full bg-blue-500 rounded-t"
                            style={{ height: `${(trend.views / maxTrend) * 150}px` }}
                            title={`Views: ${trend.views.toLocaleString()}`}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground">{trend.month}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-center gap-6 pt-4 border-t">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-blue-500 rounded" />
                      <span className="text-sm">Views</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-green-500 rounded" />
                      <span className="text-sm">Downloads</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-purple-500 rounded" />
                      <span className="text-sm">Citations</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* AI Insights */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-yellow-500" />
                  AI Insights
                </CardTitle>
                <CardDescription>Automated analysis and recommendations</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {aiInsights.map((insight, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border ${
                      insight.impact === "high"
                        ? "border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950"
                        : insight.impact === "positive"
                        ? "border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950"
                        : "border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-950"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {insight.type === "trend" && <TrendingUp className="h-4 w-4 text-red-500 mt-0.5" />}
                      {insight.type === "opportunity" && <Target className="h-4 w-4 text-yellow-500 mt-0.5" />}
                      {insight.type === "benchmark" && <Award className="h-4 w-4 text-green-500 mt-0.5" />}
                      {insight.type === "action" && <Sparkles className="h-4 w-4 text-yellow-500 mt-0.5" />}
                      <div>
                        <p className="font-medium text-sm">{insight.title}</p>
                        <p className="text-xs text-muted-foreground mt-1">{insight.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="records" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Top Performing Records</CardTitle>
              <CardDescription>Most viewed and downloaded research outputs</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {topRecords.map((record, index) => (
                  <div
                    key={record.id}
                    className="flex items-center gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-bold">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{record.title}</p>
                      <Badge variant="outline" className="mt-1">
                        {record.type}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-4 gap-6 text-center">
                      <div>
                        <p className="text-lg font-semibold">{(record.views / 1000).toFixed(1)}K</p>
                        <p className="text-xs text-muted-foreground">Views</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold">{(record.downloads / 1000).toFixed(1)}K</p>
                        <p className="text-xs text-muted-foreground">Downloads</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold">{record.citations}</p>
                        <p className="text-xs text-muted-foreground">Citations</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold">{record.altmetric}</p>
                        <p className="text-xs text-muted-foreground">Altmetric</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="geographic" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Geographic Distribution</CardTitle>
                <CardDescription>User access by country</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {geographicData.map((country) => (
                  <div key={country.country} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{country.country}</span>
                      <span className="text-sm text-muted-foreground">
                        {country.count.toLocaleString()} ({country.percentage}%)
                      </span>
                    </div>
                    <Progress value={country.percentage} className="h-2" />
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Access Patterns</CardTitle>
                <CardDescription>How users interact with your data</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <BookOpen className="h-5 w-5 text-blue-500" />
                      <span>Direct Access</span>
                    </div>
                    <span className="font-semibold">45%</span>
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Globe className="h-5 w-5 text-green-500" />
                      <span>Search Engines</span>
                    </div>
                    <span className="font-semibold">32%</span>
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Share2 className="h-5 w-5 text-purple-500" />
                      <span>Social Media</span>
                    </div>
                    <span className="font-semibold">15%</span>
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Database className="h-5 w-5 text-orange-500" />
                      <span>API Access</span>
                    </div>
                    <span className="font-semibold">8%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="researchers" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Researcher Performance</CardTitle>
              <CardDescription>Individual researcher metrics and impact</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {researcherMetrics.map((researcher) => (
                  <div
                    key={researcher.id}
                    className="flex items-center gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/40 flex items-center justify-center">
                      <Users className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{researcher.name}</p>
                      <p className="text-sm text-muted-foreground font-mono">{researcher.orcid}</p>
                    </div>
                    <div className="grid grid-cols-4 gap-6 text-center">
                      <div>
                        <p className="text-lg font-semibold">{researcher.publications}</p>
                        <p className="text-xs text-muted-foreground">Publications</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold">{researcher.citations.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground">Citations</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold">{researcher.hIndex}</p>
                        <p className="text-xs text-muted-foreground">h-Index</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold">{(researcher.views / 1000).toFixed(0)}K</p>
                        <p className="text-xs text-muted-foreground">Views</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
